import { useQueries, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowRight,
	Building2,
	CheckCircle2,
	ClipboardCheck,
	Clock,
	FileText,
	Loader2,
	Upload,
} from "lucide-react";
import { VendorLayout } from "@/components/layouts/VendorLayout";
import { useBuyerContext } from "@/contexts/vendor-buyer-context";
import { useTRPC } from "@/integrations/trpc/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

export const Route = createFileRoute("/vendor/$buyerSlug/onboarding/")({
	component: VendorOnboardingListPage,
});

interface OrgWithProgress {
	id: string;
	name: string;
	type: string;
	parentId: string | null;
	parentName?: string;
	progress: {
		forms: { total: number; completed: number };
		documents: { total: number; completed: number };
		compliance: { total: number; completed: number };
		overall: { total: number; completed: number; percentage: number };
	};
}

function getStatusFromProgress(progress: OrgWithProgress["progress"]): "complete" | "in_progress" | "not_started" {
	if (progress.overall.total === 0) return "complete"; // No requirements means complete
	if (progress.overall.percentage === 100) return "complete";
	if (progress.overall.completed > 0) return "in_progress";
	return "not_started";
}

function getStatusBadge(status: string) {
	switch (status) {
		case "complete":
			return <Badge className="bg-green-100 text-green-700">Complete</Badge>;
		case "in_progress":
			return (
				<Badge className="bg-yellow-100 text-yellow-700">In Progress</Badge>
			);
		case "not_started":
			return <Badge className="bg-gray-100 text-gray-700">Not Started</Badge>;
		default:
			return <Badge variant="outline">{status}</Badge>;
	}
}

function VendorOnboardingListPage() {
	const buyer = useBuyerContext();
	const { buyerSlug } = Route.useParams();
	const trpc = useTRPC();

	// Fetch all organizations for this buyer
	const { data: orgsData, isLoading: orgsLoading } = useQuery(
		trpc.vendorPortal.onboarding.getOrgsForBuyer.queryOptions({ buyerId: buyer.id })
	);

	// Build flat list of all orgs
	const allOrgs = orgsData ? [
		{ ...orgsData.buyer, parentId: null as string | null },
		...orgsData.members.map(m => ({ ...m, parentId: orgsData.buyer.id })),
		...orgsData.subtiers.map(s => ({ ...s, parentId: s.parent_id })),
	] : [];

	// Create a map for parent names
	const orgNameMap = Object.fromEntries(allOrgs.map(o => [o.id, o.name]));

	// Fetch progress for each org using useQueries (proper hook usage)
	const progressQueries = useQueries({
		queries: allOrgs.map(org => ({
			...trpc.vendorPortal.onboarding.getVendorProgress.queryOptions({ organizationId: org.id }),
			enabled: !!orgsData,
		})),
	});

	const isLoadingProgress = progressQueries.some(q => q.isLoading);

	// Combine org data with progress
	const orgsWithProgress: OrgWithProgress[] = allOrgs.map((org, index) => {
		const progressData = progressQueries[index]?.data;
		return {
			id: org.id,
			name: org.name,
			type: org.type,
			parentId: org.parentId,
			parentName: org.parentId ? orgNameMap[org.parentId] : undefined,
			progress: progressData ?? {
				forms: { total: 0, completed: 0 },
				documents: { total: 0, completed: 0 },
				compliance: { total: 0, completed: 0 },
				overall: { total: 0, completed: 0, percentage: 100 },
			},
		};
	});

	// Filter to only show orgs that have requirements
	const orgsToShow = orgsWithProgress.filter(org => org.progress.overall.total > 0);

	const completeCount = orgsToShow.filter(
		(o) => getStatusFromProgress(o.progress) === "complete",
	).length;
	const inProgressCount = orgsToShow.filter(
		(o) => getStatusFromProgress(o.progress) === "in_progress",
	).length;
	const notStartedCount = orgsToShow.filter(
		(o) => getStatusFromProgress(o.progress) === "not_started",
	).length;

	if (orgsLoading || isLoadingProgress) {
		return (
			<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
				<div className="min-h-[400px] flex items-center justify-center">
					<Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			</VendorLayout>
		);
	}

	return (
		<VendorLayout buyerName={buyer.name} buyerSlug={buyerSlug}>
			<div className="space-y-6">
				{/* Header */}
				<div>
					<h1 className="text-3xl font-bold tracking-tight">
						Vendor Onboarding
					</h1>
					<p className="text-muted-foreground">
						Complete onboarding requirements for organizations you want to work
						with
					</p>
				</div>

				{/* Stats */}
				<div className="grid gap-4 md:grid-cols-3">
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<CheckCircle2 className="h-4 w-4 text-green-500" />
								<span className="text-sm text-muted-foreground">Complete</span>
							</div>
							<p className="text-2xl font-bold mt-1">{completeCount}</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Clock className="h-4 w-4 text-yellow-500" />
								<span className="text-sm text-muted-foreground">
									In Progress
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">{inProgressCount}</p>
						</CardContent>
					</Card>
					<Card>
						<CardContent className="pt-6">
							<div className="flex items-center gap-2">
								<Building2 className="h-4 w-4 text-gray-500" />
								<span className="text-sm text-muted-foreground">
									Not Started
								</span>
							</div>
							<p className="text-2xl font-bold mt-1">{notStartedCount}</p>
						</CardContent>
					</Card>
				</div>

				{/* Organizations List */}
				<Card>
					<CardHeader>
						<CardTitle>Organizations</CardTitle>
						<CardDescription>
							Complete the onboarding requirements for each organization to
							become an approved vendor
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						{orgsToShow.length === 0 ? (
							<div className="text-center py-8 text-muted-foreground">
								<Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
								<p>No onboarding requirements configured for this buyer.</p>
								<p className="text-sm mt-1">
									Contact the buyer to set up vendor onboarding requirements.
								</p>
							</div>
						) : (
							orgsToShow.map((org) => {
								const status = getStatusFromProgress(org.progress);
								return (
									<div
										key={org.id}
										className={`p-4 border rounded-lg ${
											status === "complete"
												? "bg-green-50 border-green-200"
												: ""
										}`}
									>
										<div className="flex items-start justify-between mb-3">
											<div className="flex items-start gap-3">
												<Building2
													className={`h-5 w-5 mt-0.5 ${status === "complete" ? "text-green-600" : "text-muted-foreground"}`}
												/>
												<div>
													<div className="flex items-center gap-2">
														<span className="font-medium">{org.name}</span>
														{getStatusBadge(status)}
														<Badge variant="outline" className="text-xs">
															{org.type}
														</Badge>
													</div>
													{org.parentName && (
														<p className="text-sm text-muted-foreground">
															Part of {org.parentName}
														</p>
													)}
												</div>
											</div>
											{status !== "complete" ? (
												<Button asChild>
													<Link
														to="/vendor/$buyerSlug/onboarding/$orgId"
														params={{ buyerSlug, orgId: org.id }}
													>
														{status === "in_progress" ? "Continue" : "Start"}
														<ArrowRight className="ml-2 h-4 w-4" />
													</Link>
												</Button>
											) : (
												<Button variant="outline" asChild>
													<Link
														to="/vendor/$buyerSlug/onboarding/$orgId"
														params={{ buyerSlug, orgId: org.id }}
													>
														View
													</Link>
												</Button>
											)}
										</div>

										{/* Progress */}
										{status !== "complete" && (
											<div className="space-y-3">
												<div className="flex items-center gap-2">
													<Progress value={org.progress.overall.percentage} className="flex-1" />
													<span className="text-sm text-muted-foreground w-12">
														{org.progress.overall.percentage}%
													</span>
												</div>

												<div className="grid grid-cols-3 gap-4 text-sm">
													<div className="flex items-center gap-2">
														<FileText className="h-4 w-4 text-muted-foreground" />
														<span>
															Forms: {org.progress.forms.completed}/
															{org.progress.forms.total}
														</span>
													</div>
													<div className="flex items-center gap-2">
														<Upload className="h-4 w-4 text-muted-foreground" />
														<span>
															Docs: {org.progress.documents.completed}/
															{org.progress.documents.total}
														</span>
													</div>
													<div className="flex items-center gap-2">
														<ClipboardCheck className="h-4 w-4 text-muted-foreground" />
														<span>
															Compliance: {org.progress.compliance.completed}/
															{org.progress.compliance.total}
														</span>
													</div>
												</div>
											</div>
										)}

										{status === "complete" && (
											<div className="flex items-center gap-4 text-sm text-green-700">
												<span className="flex items-center gap-1">
													<CheckCircle2 className="h-4 w-4" />
													{org.progress.forms.total} forms
												</span>
												<span className="flex items-center gap-1">
													<CheckCircle2 className="h-4 w-4" />
													{org.progress.documents.total} documents
												</span>
												<span className="flex items-center gap-1">
													<CheckCircle2 className="h-4 w-4" />
													{org.progress.compliance.total} compliance checks
												</span>
											</div>
										)}
									</div>
								);
							})
						)}
					</CardContent>
				</Card>
			</div>
		</VendorLayout>
	);
}
